/**
 * Date Transformation Utilities
 *
 * Utilities for transforming date strings from API responses to local Date objects.
 * Solves the timezone issue where ISO date strings like "2026-01-21" are interpreted
 * as UTC midnight, causing dates to shift by one day in timezones behind UTC.
 *
 * @example
 * // API returns: { date: "2026-01-21" }
 * // In UTC-3: new Date("2026-01-21") = Jan 20, 21:00 (wrong!)
 * // With parseISODateToLocal: Jan 21, 00:00 (correct!)
 */

/** Regex pattern for ISO date format YYYY-MM-DD */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Regex pattern for full ISO timestamp YYYY-MM-DDTHH:mm:ss.sssZ */
const ISO_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

/**
 * Type guard to check if a value is a valid ISO date string (YYYY-MM-DD).
 *
 * @param value - The value to check
 * @returns True if the value is a valid ISO date string
 *
 * @example
 * isISODateString("2026-01-21") // true
 * isISODateString("2026-1-21")  // false (invalid format)
 * isISODateString(new Date())   // false (not a string)
 */
export function isISODateString(value: unknown): value is string {
  return typeof value === "string" && ISO_DATE_PATTERN.test(value);
}

/**
 * Type guard to check if a value is a full ISO timestamp string.
 *
 * @param value - The value to check
 * @returns True if the value is an ISO timestamp (e.g., "2026-01-21T00:00:00.000Z")
 *
 * @example
 * isISOTimestamp("2026-01-05T00:00:00.000Z") // true
 * isISOTimestamp("2026-01-21")               // false (date only)
 */
export function isISOTimestamp(value: unknown): value is string {
  return typeof value === "string" && ISO_TIMESTAMP_PATTERN.test(value);
}

/**
 * Parse an ISO date string (YYYY-MM-DD) to a local Date object.
 * Uses explicit year/month/day parsing to avoid timezone issues.
 *
 * @param dateString - ISO date string in YYYY-MM-DD format
 * @returns Date object set to midnight in the local timezone
 * @throws Error if the date string is invalid
 *
 * @example
 * parseISODateToLocal("2026-01-21") // Date: Jan 21, 2026 00:00:00 (local)
 */
export function parseISODateToLocal(dateString: string): Date {
  if (!isISODateString(dateString)) {
    throw new Error(
      `Invalid ISO date string: "${dateString}". Expected format: YYYY-MM-DD`
    );
  }

  const [year, month, day] = dateString.split("-").map(Number);

  // Validate parsed values
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    throw new Error(`Failed to parse date components from: "${dateString}"`);
  }

  // Validate date ranges
  if (month < 1 || month > 12) {
    throw new Error(`Invalid month: ${month}. Expected 1-12`);
  }

  if (day < 1 || day > 31) {
    throw new Error(`Invalid day: ${day}. Expected 1-31`);
  }

  return new Date(year, month - 1, day);
}

/**
 * Parse a full ISO timestamp (YYYY-MM-DDTHH:mm:ss.sssZ) to a local Date object.
 * Extracts only the date portion to avoid timezone shift issues.
 *
 * @param timestamp - Full ISO timestamp string
 * @returns Date object set to midnight in the local timezone
 *
 * @example
 * parseISOTimestampToLocal("2026-01-05T00:00:00.000Z") // Date: Jan 5, 2026 00:00:00 (local)
 * // In UTC-3: new Date("2026-01-05T00:00:00.000Z") = Jan 4, 21:00 (wrong!)
 * // With parseISOTimestampToLocal: Jan 5, 00:00 (correct!)
 */
export function parseISOTimestampToLocal(timestamp: string): Date {
  // Extract the date portion (YYYY-MM-DD) from the timestamp
  const datePart = timestamp.split("T")[0];
  return parseISODateToLocal(datePart);
}

/**
 * Safely parse a date value that may be a string or already a Date.
 * Handles both YYYY-MM-DD format and full ISO timestamps.
 * Returns the original value if it's already a Date, parses if it's a string.
 *
 * @param value - Date string, ISO timestamp, or Date object
 * @returns Date object in local timezone
 *
 * @example
 * normalizeDateToLocal("2026-01-21")              // Date: Jan 21, 2026
 * normalizeDateToLocal("2026-01-05T00:00:00.000Z") // Date: Jan 5, 2026
 * normalizeDateToLocal(new Date())                // Returns the same Date
 * normalizeDateToLocal("invalid")                 // Returns current date (fallback)
 */
export function normalizeDateToLocal(value: string | Date | unknown): Date {
  // Already a Date object - return as-is
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? new Date() : value;
  }

  // Full ISO timestamp - extract date and parse to local
  if (isISOTimestamp(value)) {
    return parseISOTimestampToLocal(value);
  }

  // ISO date string - parse to local
  if (isISODateString(value)) {
    return parseISODateToLocal(value);
  }

  // Invalid input - return current date as fallback
  return new Date();
}

/**
 * Serialize a Date (or date-like value) to YYYY-MM-DD using local calendar fields.
 * This avoids UTC conversion side-effects from `toISOString()` for date-only fields.
 */
export function formatLocalDateToISODate(value: string | Date | unknown): string {
  const date = normalizeDateToLocal(value);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/** UTC midnight, the form that `new Date()` shifts to the previous day in Brazil. */
const UTC_MIDNIGHT_ISO_PATTERN = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.\d{1,3})?Z$/;

/**
 * Keep a calendar date stable when a list formats it with `new Date(value)`.
 *
 * Date-only strings and UTC midnight are the previous day in America/Sao_Paulo.
 * Noon UTC stays on the same calendar day from UTC-12 through UTC+11.
 * Other timestamps are left unchanged.
 */
export function stabilizeCalendarDateForListDisplay(value: string): string {
  if (isISODateString(value)) {
    return `${value}T12:00:00.000Z`;
  }

  const utcMidnight = UTC_MIDNIGHT_ISO_PATTERN.exec(value);
  if (utcMidnight) {
    return `${utcMidnight[1]}T12:00:00.000Z`;
  }

  return value;
}

export function stabilizeTransactionListDates<
  T extends { date: string; dueDate?: string | null },
>(items: T[]): T[] {
  return items.map((item) => ({
    ...item,
    date: stabilizeCalendarDateForListDisplay(item.date),
    ...(typeof item.dueDate === "string"
      ? { dueDate: stabilizeCalendarDateForListDisplay(item.dueDate) }
      : {}),
  }));
}

/**
 * Generic interface for objects with a date field that can be transformed.
 */
export interface WithDate {
  date: string | Date;
}

/**
 * Transform an array of objects with date fields from ISO strings to Date objects.
 * Preserves all other properties of each object.
 *
 * @param items - Array of objects with date fields
 * @returns New array with dates transformed to local Date objects
 *
 * @example
 * const occurrences = [{ date: "2026-01-21", startTime: "09:00" }];
 * transformDateFields(occurrences);
 * // [{ date: Date(2026-01-21), startTime: "09:00" }]
 */
export function transformDateFields<T extends WithDate>(items: T[]): T[] {
  return items.map((item) => ({
    ...item,
    date: normalizeDateToLocal(item.date),
  }));
}

/**
 * Activity occurrence structure from API.
 * Dates may be ISO date strings (YYYY-MM-DD) or full ISO timestamps (YYYY-MM-DDTHH:mm:ss.sssZ).
 */
interface ActivityOccurrence {
  date: string | Date;
  startTime?: string;
  endTime?: string;
  [key: string]: unknown;
}

/**
 * Activity structure with occurrences array to transform.
 */
interface ActivityWithOccurrences {
  occurrences?:
    | ActivityOccurrence[]
    | { count?: number; list?: ActivityOccurrence[] };
  [key: string]: unknown;
}

/**
 * Transform activity occurrence dates from ISO strings to local Date objects.
 * Handles both array format and { count, list } format from API responses.
 *
 * Fixes timezone issues where UTC dates (e.g., "2026-01-05T00:00:00.000Z")
 * are incorrectly displayed one day earlier in timezones behind UTC.
 *
 * @param activity - Activity object with occurrences
 * @returns New activity object with occurrence dates transformed
 *
 * @example
 * const activity = {
 *   id: "123",
 *   occurrences: [{ date: "2026-01-05T00:00:00.000Z", startTime: "19:00" }]
 * };
 * transformActivityDates(activity);
 * // { id: "123", occurrences: [{ date: Date(2026-01-05), startTime: "19:00" }] }
 */
export function transformActivityDates<T extends ActivityWithOccurrences>(
  activity: T
): T {
  if (!activity.occurrences) {
    return activity;
  }

  // Handle { count, list } format
  if (
    typeof activity.occurrences === "object" &&
    !Array.isArray(activity.occurrences) &&
    "list" in activity.occurrences &&
    Array.isArray(activity.occurrences.list)
  ) {
    return {
      ...activity,
      occurrences: {
        ...activity.occurrences,
        list: transformDateFields(activity.occurrences.list),
      },
    };
  }

  // Handle direct array format
  if (Array.isArray(activity.occurrences)) {
    return {
      ...activity,
      occurrences: transformDateFields(activity.occurrences),
    };
  }

  return activity;
}
